import { useQuery } from '@tanstack/react-query'
import { Link, useParams } from 'react-router-dom'
import { fetchData } from '../../api/fetchJson'
import { orgPath } from '../../config/orgId'
import { Card, Currency, ErrorBanner, Loading, PageHeader, Tag } from '../../components/ui'

// Mirrors src/schemas/accounts-summary.ts. Breakdowns are arrays of
// CategoryBreakdownItem (one row per category in scope) — NOT the old
// fixed-key object the demo used to render.
type CategoryBreakdownItem = {
  category_id: string
  category_name: string
  category_slug: string | null
  category_kind: 'expense' | 'income' | 'both'
  seeded: boolean
  total: number
  last_entry_date: number | null
}

type SourceBuckets = { self: number; client: number; other: number; transfer: number }

// GST side bifurcation (#450 / A22): the summary aggregates vendor-bill GST,
// i.e. the Input (input-tax-credit) side. Labels are constant on this block.
type GstSummary = {
  gst_bucket: 'input_gst'
  gst_label: string
  total_gst: number
  cgst: number
  sgst: number
  igst: number
  gst_inclusive_bills: number
  gst_exclusive_bills: number
}

type AccountsSummary = {
  total_income?: number
  total_expense?: number
  paid_expense?: number
  unpaid_expense?: number
  balance_in_account?: number
  balance_in_cash?: number
  third_party_balance?: number
  income_by_source?: { cash: SourceBuckets; bank: SourceBuckets }
  income_breakdown?: CategoryBreakdownItem[]
  expense_breakdown?: CategoryBreakdownItem[]
  gst_summary?: GstSummary
}

export function AccountsSummaryPage() {
  const { projectId } = useParams<{ projectId: string }>()
  const q = useQuery<AccountsSummary>({
    queryKey: ['accounts-summary', projectId],
    enabled: Boolean(projectId),
    queryFn: () =>
      fetchData<AccountsSummary>(orgPath(`/projects/${projectId}/accounts/summary`)),
  })

  if (q.isLoading) return <Loading />
  if (q.isError) return <ErrorBanner error={q.error} />

  const s = q.data ?? {}
  const balance =
    s.balance_in_account !== undefined && s.balance_in_cash !== undefined
      ? (s.balance_in_account ?? 0) + (s.balance_in_cash ?? 0)
      : undefined

  const headlineTiles: Array<[string, number | undefined]> = [
    ['Expense', s.total_expense],
    ['Balance', balance],
    ['Outstanding (material)', s.unpaid_expense],
    ['Cash on hand', s.balance_in_cash],
  ]

  const expenseRows = (s.expense_breakdown ?? []).filter((r) => r.total !== 0)
  const incomeRows = (s.income_breakdown ?? []).filter((r) => r.total !== 0)
  const src = s.income_by_source
  const gst = s.gst_summary

  return (
    <div className="space-y-6">
      <PageHeader title="Accounts summary" />

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
        {/* Income tile is a click-through into the drill-down (A20 / #449). */}
        <Link to="income" className="block group">
          <Card className="h-full group-hover:border-accent-500">
            <div className="text-xs uppercase tracking-wide text-surface-400 flex items-center gap-1">
              Income <span className="text-accent-400">›</span>
            </div>
            <div className="text-xl font-semibold mt-1">
              <Currency amount={s.total_income ?? null} />
            </div>
            <div className="text-[11px] text-surface-400 mt-1">View drill-down</div>
          </Card>
        </Link>
        {headlineTiles.map(([label, value]) => (
          <Card key={label}>
            <div className="text-xs uppercase tracking-wide text-surface-400">{label}</div>
            <div className="text-xl font-semibold mt-1">
              <Currency amount={value ?? null} />
            </div>
          </Card>
        ))}
      </div>

      {gst && (
        <Card>
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <h2 className="font-semibold">GST</h2>
              <Tag tone="warn">{gst.gst_label}</Tag>
            </div>
            <Link to="gst" className="text-sm text-accent-400 hover:underline">
              Full GST report ›
            </Link>
          </div>
          <p className="text-xs text-surface-400 mb-3">
            Aggregated from this project&rsquo;s vendor bills (input-tax-credit side).
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
            <div>
              <div className="text-xs text-surface-400">Total GST</div>
              <div className="font-semibold mt-0.5"><Currency amount={gst.total_gst} /></div>
            </div>
            <div>
              <div className="text-xs text-surface-400">CGST</div>
              <div className="mt-0.5"><Currency amount={gst.cgst} /></div>
            </div>
            <div>
              <div className="text-xs text-surface-400">SGST</div>
              <div className="mt-0.5"><Currency amount={gst.sgst} /></div>
            </div>
            <div>
              <div className="text-xs text-surface-400">IGST</div>
              <div className="mt-0.5"><Currency amount={gst.igst} /></div>
            </div>
          </div>
          <div className="text-xs text-surface-400 mt-3">
            {gst.gst_inclusive_bills} bill(s) with GST · {gst.gst_exclusive_bills} without
          </div>
        </Card>
      )}

      {src && (
        <Card>
          <h2 className="font-semibold mb-3">Income by source</h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-surface-400 border-b border-surface-700">
                  <th className="py-2 pr-4">Landing</th>
                  <th className="py-2 pr-4 text-right">Self</th>
                  <th className="py-2 pr-4 text-right">Client</th>
                  <th className="py-2 pr-4 text-right">Other</th>
                  <th className="py-2 pr-4 text-right">Site Transfer</th>
                  <th className="py-2 text-right">Total</th>
                </tr>
              </thead>
              <tbody>
                {(['cash', 'bank'] as const).map((landing) => {
                  const b = src[landing]
                  const rowTotal = b.self + b.client + b.other + b.transfer
                  return (
                    <tr key={landing} className="border-b border-surface-700/50">
                      <td className="py-2 pr-4 capitalize">{landing}</td>
                      <td className="py-2 pr-4 text-right"><Currency amount={b.self} /></td>
                      <td className="py-2 pr-4 text-right"><Currency amount={b.client} /></td>
                      <td className="py-2 pr-4 text-right"><Currency amount={b.other} /></td>
                      <td className="py-2 pr-4 text-right"><Currency amount={b.transfer} /></td>
                      <td className="py-2 text-right font-medium"><Currency amount={rowTotal} /></td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <div className="grid gap-6 lg:grid-cols-2">
        {incomeRows.length > 0 && (
          <Card>
            <h2 className="font-semibold mb-3">Income breakdown</h2>
            <ul className="divide-y divide-surface-700 text-sm">
              {incomeRows.map((r) => (
                <li key={r.category_id} className="py-2 flex justify-between gap-3">
                  <span>{r.category_name}</span>
                  <Currency amount={r.total} />
                </li>
              ))}
            </ul>
          </Card>
        )}

        {expenseRows.length > 0 && (
          <Card>
            <h2 className="font-semibold mb-3">Expense breakdown</h2>
            <ul className="divide-y divide-surface-700 text-sm">
              {expenseRows.map((r) => (
                <li key={r.category_id} className="py-2 flex justify-between gap-3">
                  <span>{r.category_name}</span>
                  <Currency amount={r.total} />
                </li>
              ))}
            </ul>
          </Card>
        )}
      </div>
    </div>
  )
}
